import type {
  ApiCloudTheme,
  ApiCloudThemeSettings,
  ApiPrivacySettings,
  ApiUsername,
  ApiWallpaperSettings,
} from '../../../api/types';
import type { ThemeKey } from '../../../types';
import type { ActionReturnType, GlobalState } from '../../types';
import { ApiMediaFormat } from '../../../api/types';
import { ProfileEditProgress, UPLOADING_WALLPAPER_SLUG } from '../../../types';

import {
  APP_CONFIG_REFETCH_INTERVAL,
  COUNTRIES_WITH_12H_TIME_FORMAT,
  CUSTOM_BG_CACHE_NAME,
  DARK_THEME_PATTERN_BG_COLOR,
  DARK_THEME_PATTERN_COLOR,
  DEBUG,
  DEFAULT_PATTERN_COLOR,
  LIGHT_THEME_BG_COLOR,
  MAX_INT_32,
} from '../../../config';
import * as cacheApi from '../../../util/cacheApi';
import {
  CLOUD_THEME_FORMAT,
  findCloudTheme,
  findCloudThemeById,
  resolveCloudThemeSettings,
} from '../../../util/cloudThemes';
import { getAverageColor, getPatternColor, hex2rgb, rgb2hex } from '../../../util/colors';
import { logDebugMessage } from '../../../util/debugConsole';
import { buildDebugLogSignature } from '../../../util/debugLogSignature';
import { getCurrentTabId } from '../../../util/establishMultitabRole';
import { fetchBlob } from '../../../util/files';
import { buildCollectionByKey } from '../../../util/iteratees';
import * as mediaLoader from '../../../util/mediaLoader';
import { requestPermission, subscribe, unsubscribe } from '../../../util/notifications';
import { setTimeFormat } from '../../../util/oldLangProvider';
import requestActionTimeout from '../../../util/requestActionTimeout';
import { getServerTime } from '../../../util/serverTime';
import {
  buildThemeWallpaperDebugSnapshot,
  hasThemeWallpaperMutation,
  pickThemeWallpaperSettings,
} from '../../../util/themeWallpaper';
import {
  getCacheableWallpaperBlob,
  WALLPAPER_RENDERER_VERSION,
} from '../../../util/wallpaperPattern';
import { isWallpaperPattern } from '../../../util/wallpaperType';
import { callApi } from '../../../api/gramjs';
import { buildApiInputPrivacyRules } from '../../helpers';
import { addActionHandler, getGlobal, setGlobal } from '../../index';
import {
  addBlockedUser, addNotifyException, addNotifyExceptions, deletePeerPhoto,
  removeBlockedUser, replaceSettings, replaceThemeSettings, updateChat,
  updateUser, updateUserFullInfo,
} from '../../reducers';
import { updateTabState } from '../../reducers/tabs';
import {
  selectChat, selectTabState, selectUser,
} from '../../selectors';

const THEME_KEYS: ThemeKey[] = ['light', 'dark'];
type ThemeSettingsUpdate = NonNullable<Parameters<typeof replaceThemeSettings>[2]>;
const DEFAULT_PATTERN_WALLPAPER_INTENSITY = 50;
const DEFAULT_PATTERN_WALLPAPER_ROTATION = 45;
const CLOUD_WALLPAPER_SIGNATURE_PREFIX = 'cloud-wallpaper-signature';
const CLOUD_WALLPAPER_BLOB_PREFIX = 'cloud-wallpaper-blob';

let isCloudThemeDocumentsSynced = false;
let cloudThemeThemeSettingsSnapshot: Partial<Record<ThemeKey, ThemeSettingsUpdate>> | undefined;
const wallpaperThumbnailColorCache = new Map<string, string | undefined>();
const lastCloudThemeLogSignatureByEvent = new Map<string, string>();
const NON_DEDUP_CLOUD_THEME_LOG_EVENTS = new Set([
  'cacheCloudWallpaper:failed-after-retry',
  'cacheCloudWallpaper:mediaLoader-threw',
]);

function logCloudThemeWallpaper(event: string, details?: AnyLiteral) {
  if (!DEBUG) {
    return;
  }

  const payload = details || {};
  if (!NON_DEDUP_CLOUD_THEME_LOG_EVENTS.has(event)) {
    const signature = buildDebugLogSignature(payload);
    if (lastCloudThemeLogSignatureByEvent.get(event) === signature) {
      return;
    }
    lastCloudThemeLogSignatureByEvent.set(event, signature);
  }

  logDebugMessage('debug', '[CloudThemeWallpaper]', event, payload);
}

function applyFetchedCloudThemes<T extends GlobalState>(global: T, result: {
  hash: string;
  themes: ApiCloudTheme[];
}) {
  const cloudThemeId = global.settings.byKey.cloudThemeId;
  const selectedTheme = findCloudThemeById(result.themes, cloudThemeId);

  return {
    selectedTheme,
    global: {
      ...global,
      settings: {
        ...global.settings,
        byKey: {
          ...global.settings.byKey,
          cloudThemeId: selectedTheme?.id,
        },
        cloudThemes: {
          hash: result.hash,
          themes: result.themes,
        },
      },
    } as T,
  };
}

function getDefaultThemeSettings(themeKey: ThemeKey): ThemeSettingsUpdate {
  return {
    background: undefined,
    backgroundDocumentId: undefined,
    backgroundColor: undefined,
    patternColor: themeKey === 'dark' ? DARK_THEME_PATTERN_COLOR : DEFAULT_PATTERN_COLOR,
    isBlurred: true,
    wallpaperSettings: undefined,
    isPatternWallpaper: undefined,
    isDarkWallpaper: undefined,
    wallpaperMimeType: undefined,
    isWallpaperOverridden: false,
  };
}

function captureThemeSettingsSnapshot(global: GlobalState) {
  const snapshot: Partial<Record<ThemeKey, ThemeSettingsUpdate>> = {};

  THEME_KEYS.forEach((themeKey) => {
    snapshot[themeKey] = pickThemeWallpaperSettings(global.settings.themes[themeKey]);
  });

  cloudThemeThemeSettingsSnapshot = snapshot;
  logCloudThemeWallpaper('snapshot:capture', {
    themes: snapshot,
  });
}

function restoreThemeSettingsFromSnapshot<T extends GlobalState>(global: T): T {
  let updatedGlobal = global;

  THEME_KEYS.forEach((themeKey) => {
    const snapshotSettings = cloudThemeThemeSettingsSnapshot?.[themeKey];
    const restoreSettings = snapshotSettings && Object.keys(snapshotSettings).length
      ? snapshotSettings
      : getDefaultThemeSettings(themeKey);

    updatedGlobal = replaceThemeSettings(updatedGlobal, themeKey, restoreSettings);
  });

  logCloudThemeWallpaper('snapshot:restore', {
    hasSnapshot: Boolean(cloudThemeThemeSettingsSnapshot),
  });
  cloudThemeThemeSettingsSnapshot = undefined;

  return updatedGlobal;
}

addActionHandler('updateProfile', async (global, actions, payload): Promise<void> => {
  const {
    photo, firstName, lastName, bio: about, username,
    tabId = getCurrentTabId(),
  } = payload;

  const { currentUserId } = global;
  if (!currentUserId) {
    return;
  }

  global = updateTabState(global, {
    profileEdit: {
      progress: ProfileEditProgress.InProgress,
    },
  }, tabId);
  setGlobal(global);

  if (photo) {
    await callApi('uploadProfilePhoto', photo);
  }

  if (firstName || lastName || about) {
    const result = await callApi('updateProfile', { firstName, lastName, about });
    if (result) {
      global = getGlobal();
      const currentUser = currentUserId && selectUser(global, currentUserId);

      if (currentUser) {
        global = updateUser(
          global,
          currentUser.id,
          {
            firstName,
            lastName,
          },
        );
        global = updateUserFullInfo(global, currentUser.id, { bio: about });
        setGlobal(global);
      }
    }
  }

  if (username !== undefined) {
    const result = await callApi('updateUsername', username);
    global = getGlobal();
    const currentUser = currentUserId && selectUser(global, currentUserId);

    if (result && currentUser) {
      const shouldUsernameUpdate = currentUser.usernames?.find((u) => u.isEditable);
      const usernames = shouldUsernameUpdate
        ? currentUser.usernames?.map((u) => (u.isEditable ? { ...u, username } : u))
        : [{ username, isEditable: true, isActive: true } as ApiUsername, ...currentUser.usernames || []];
      global = updateUser(global, currentUserId, { usernames });
      setGlobal(global);
    }
  }

  global = getGlobal();
  global = updateTabState(global, {
    profileEdit: {
      progress: ProfileEditProgress.Complete,
    },
  }, tabId);
  setGlobal(global);

  if (photo) {
    actions.loadFullUser({ userId: currentUserId, withPhotos: true });
  }
});

addActionHandler('updateProfilePhoto', async (global, actions, payload): Promise<void> => {
  const { photo, isFallback } = payload;
  const { currentUserId } = global;
  if (!currentUserId) return;
  const currentUser = selectUser(global, currentUserId);
  if (!currentUser) return;

  global = updateUser(global, currentUserId, { avatarPhotoId: undefined });
  global = updateUserFullInfo(global, currentUserId, { profilePhoto: undefined });

  setGlobal(global);

  const result = await callApi('updateProfilePhoto', photo, isFallback);
  if (!result) return;

  actions.loadFullUser({ userId: currentUserId, withPhotos: true });
});

addActionHandler('deleteProfilePhoto', async (global, actions, payload): Promise<void> => {
  const { photo } = payload;
  const { currentUserId } = global;
  if (!currentUserId) return;

  const isDeleted = await callApi('deleteProfilePhotos', [photo]);
  if (!isDeleted) return;

  global = getGlobal();
  global = deletePeerPhoto(global, currentUserId, photo.id);
  setGlobal(global);

  actions.loadFullUser({ userId: currentUserId, withPhotos: true });
});

addActionHandler('checkUsername', async (global, actions, payload): Promise<void> => {
  const { username, tabId = getCurrentTabId() } = payload!;

  let tabState = selectTabState(global, tabId);
  // No need to check the username if profile update is already in progress
  if (tabState.profileEdit && tabState.profileEdit.progress === ProfileEditProgress.InProgress) {
    return;
  }

  global = updateTabState(global, {
    profileEdit: {
      progress: tabState.profileEdit ? tabState.profileEdit.progress : ProfileEditProgress.Idle,
      checkedUsername: undefined,
      isUsernameAvailable: undefined,
      error: undefined,
    },
  }, tabId);
  setGlobal(global);

  const { result, error } = (await callApi('checkUsername', username))!;

  global = getGlobal();
  tabState = selectTabState(global, tabId);
  global = updateTabState(global, {
    profileEdit: {
      ...tabState.profileEdit!,
      checkedUsername: username,
      isUsernameAvailable: result === true,
      error,
    },
  }, tabId);
  setGlobal(global);
});

addActionHandler('loadWallpapers', async (global): Promise<void> => {
  const result = await callApi('fetchWallpapers');
  if (!result) {
    return;
  }

  global = getGlobal();
  global = {
    ...global,
    settings: {
      ...global.settings,
      loadedWallpapers: result.wallpapers,
    },
  };
  setGlobal(global);
});

addActionHandler('uploadWallpaper', async (global, actions, payload): Promise<void> => {
  const file = payload;
  const previewBlobUrl = URL.createObjectURL(file);

  global = {
    ...global,
    settings: {
      ...global.settings,
      loadedWallpapers: [
        {
          slug: UPLOADING_WALLPAPER_SLUG,
          document: {
            mediaType: 'document',
            fileName: '',
            size: file.size,
            mimeType: file.type,
            previewBlobUrl,
          },
        },
        ...(global.settings.loadedWallpapers || []),
      ],
    },
  };
  setGlobal(global);

  const result = await callApi('uploadWallpaper', file);
  if (!result) {
    return;
  }

  const { wallpaper } = result;

  global = getGlobal();
  if (!global.settings.loadedWallpapers) {
    return;
  }

  const firstWallpaper = global.settings.loadedWallpapers[0];
  if (!firstWallpaper || firstWallpaper.slug !== UPLOADING_WALLPAPER_SLUG) {
    return;
  }

  const withLocalMedia = {
    ...wallpaper,
    document: {
      ...wallpaper.document,
      previewBlobUrl,
    },
  };

  global = {
    ...global,
    settings: {
      ...global.settings,
      loadedWallpapers: [
        withLocalMedia,
        ...global.settings.loadedWallpapers.slice(1),
      ],
    },
  };
  setGlobal(global);
});

addActionHandler('loadBlockedUsers', async (global): Promise<void> => {
  const result = await callApi('fetchBlockedUsers');
  if (!result) return;

  global = getGlobal();

  global = {
    ...global,
    blocked: {
      ids: result.blockedIds,
      totalCount: result.totalCount,
    },
  };

  setGlobal(global);
});

addActionHandler('blockUser', async (global, actions, payload): Promise<void> => {
  const { userId } = payload;

  const user = selectUser(global, userId);
  if (!user) return;

  const result = await callApi('blockUser', { user });
  if (!result) return;

  global = getGlobal();
  global = addBlockedUser(global, userId);
  setGlobal(global);
});

addActionHandler('unblockUser', async (global, actions, payload): Promise<void> => {
  const { userId } = payload;

  const user = selectUser(global, userId);
  if (!user) return;

  const result = await callApi('unblockUser', { user });
  if (!result) return;

  global = getGlobal();
  global = removeBlockedUser(global, userId);
  setGlobal(global);
});

addActionHandler('loadNotificationExceptions', async (global): Promise<void> => {
  const result = await callApi('fetchNotificationExceptions');
  if (!result) {
    return;
  }

  global = getGlobal();
  global = addNotifyExceptions(global, result);
  setGlobal(global);
});

addActionHandler('loadNotificationSettings', async (global): Promise<void> => {
  const [signUpNotification, notifyDefaults] = await Promise.all([
    callApi('fetchContactSignUpSetting'),
    callApi('fetchNotifyDefaultSettings'),
  ]);

  if (!notifyDefaults) return;

  global = getGlobal();
  global = replaceSettings(global, {
    hasContactJoinedNotifications: signUpNotification,
  });
  global = {
    ...global,
    settings: {
      ...global.settings,
      notifyDefaults,
    },
  };
  setGlobal(global);
});

addActionHandler('updateNotificationSettings', async (global, actions, payload): Promise<void> => {
  const { peerType, isMuted, shouldShowPreviews } = payload!;

  const result = await callApi('updateNotificationSettings', peerType, { isMuted, shouldShowPreviews });
  if (!result) {
    return;
  }

  global = getGlobal();
  global = addNotifyException(global, peerType, {
    mutedUntil: isMuted ? MAX_INT_32 : undefined,
    shouldShowPreviews,
  });
  setGlobal(global);
});

addActionHandler('updateWebNotificationSettings', async (global, actions, payload): Promise<void> => {
  const oldSettings = global.settings.byKey;
  global = replaceSettings(global, payload);
  setGlobal(global);
  const { hasWebNotifications, hasPushNotifications } = global.settings.byKey;
  if (!oldSettings.hasPushNotifications && hasPushNotifications) {
    await subscribe();
  }
  if (oldSettings.hasPushNotifications && !hasPushNotifications) {
    await unsubscribe();
  }
  if (!oldSettings.hasWebNotifications && hasWebNotifications) {
    const isGranted = await requestPermission();
    if (!isGranted) {
      global = getGlobal();
      global = replaceSettings(global, { hasWebNotifications: false });
      setGlobal(global);
    }
  }
});

addActionHandler('updateContactSignUpNotification', async (global, actions, payload): Promise<void> => {
  const { isSilent } = payload;

  const result = await callApi('updateContactSignUpNotification', isSilent);
  if (!result) {
    return;
  }

  global = getGlobal();
  global = replaceSettings(global, { hasContactJoinedNotifications: !isSilent });
  setGlobal(global);
});

addActionHandler('loadLanguages', async (global): Promise<void> => {
  const result = await callApi('fetchLanguages');
  if (!result) {
    return;
  }

  global = getGlobal();
  global = {
    ...global,
    settings: {
      ...global.settings,
      languages: result,
    },
  };
  setGlobal(global);
});

addActionHandler('loadPrivacySettings', async (global): Promise<void> => {
  const result = await Promise.all([
    callApi('fetchPrivacySettings', 'phoneNumber'),
    callApi('fetchPrivacySettings', 'addByPhone'),
    callApi('fetchPrivacySettings', 'lastSeen'),
    callApi('fetchPrivacySettings', 'profilePhoto'),
    callApi('fetchPrivacySettings', 'forwards'),
    callApi('fetchPrivacySettings', 'chatInvite'),
    callApi('fetchPrivacySettings', 'phoneCall'),
    callApi('fetchPrivacySettings', 'phoneP2P'),
    callApi('fetchPrivacySettings', 'voiceMessages'),
    callApi('fetchPrivacySettings', 'bio'),
    callApi('fetchPrivacySettings', 'birthday'),
  ]);

  if (result.some((e) => e === undefined)) {
    return;
  }

  const [
    phoneNumberSettings,
    addByPhoneSettings,
    lastSeenSettings,
    profilePhotoSettings,
    forwardsSettings,
    chatInviteSettings,
    phoneCallSettings,
    phoneP2PSettings,
    voiceMessagesSettings,
    bioSettings,
    birthdaySettings,
  ] = result as {
    rules: ApiPrivacySettings;
  }[];

  global = getGlobal();
  global = {
    ...global,
    settings: {
      ...global.settings,
      privacy: {
        ...global.settings.privacy,
        phoneNumber: phoneNumberSettings.rules,
        addByPhone: addByPhoneSettings.rules,
        lastSeen: lastSeenSettings.rules,
        profilePhoto: profilePhotoSettings.rules,
        forwards: forwardsSettings.rules,
        chatInvite: chatInviteSettings.rules,
        phoneCall: phoneCallSettings.rules,
        phoneP2P: phoneP2PSettings.rules,
        voiceMessages: voiceMessagesSettings.rules,
        bio: bioSettings.rules,
        birthday: birthdaySettings.rules,
      },
    },
  };
  setGlobal(global);
});

addActionHandler('setPrivacyVisibility', async (global, actions, payload): Promise<void> => {
  const { privacyKey, visibility, onSuccess } = payload!;

  if (!global.settings.privacy[privacyKey]) {
    const result = await callApi('fetchPrivacySettings', privacyKey);
    if (!result) {
      return;
    }

    global = getGlobal();
    global = {
      ...global,
      settings: {
        ...global.settings,
        privacy: {
          ...global.settings.privacy,
          [privacyKey]: result.rules,
        },
      },
    };
    setGlobal(global);
  }

  const {
    privacy: { [privacyKey]: settings },
  } = global.settings;

  if (!settings) {
    return;
  }

  const rules = buildApiInputPrivacyRules(global, {
    visibility,
    allowedIds: [...settings.allowUserIds, ...settings.allowChatIds],
    blockedIds: [...settings.blockUserIds, ...settings.blockChatIds],
  });

  const result = await callApi('setPrivacySettings', privacyKey, rules);
  if (!result) {
    return;
  }

  onSuccess?.();

  global = getGlobal();
  global = {
    ...global,
    settings: {
      ...global.settings,
      privacy: {
        ...global.settings.privacy,
        [privacyKey]: result.rules,
      },
    },
  };
  setGlobal(global);
});

addActionHandler('setPrivacySettings', async (global, actions, payload): Promise<void> => {
  const {
    privacyKey, isAllowList, updatedIds,
  } = payload!;
  const {
    privacy: { [privacyKey]: settings },
  } = global.settings;

  if (!settings) {
    return;
  }

  const rules = buildApiInputPrivacyRules(global, {
    visibility: settings.visibility,
    isUnspecified: settings.isUnspecified,
    allowedIds: isAllowList ? updatedIds : [...settings.allowUserIds, ...settings.allowChatIds],
    blockedIds: !isAllowList ? updatedIds : [...settings.blockUserIds, ...settings.blockChatIds],
  });

  const result = await callApi('setPrivacySettings', privacyKey, rules);
  if (!result) {
    return;
  }

  global = getGlobal();
  global = {
    ...global,
    settings: {
      ...global.settings,
      privacy: {
        ...global.settings.privacy,
        [privacyKey]: result.rules,
      },
    },
  };
  setGlobal(global);
});

addActionHandler('updateIsOnline', (global, actions, payload): ActionReturnType => {
  if (global.connectionState !== 'connectionStateReady') return;
  callApi('updateIsOnline', payload);
});

addActionHandler('loadContentSettings', async (global): Promise<void> => {
  const result = await callApi('fetchContentSettings');
  if (!result) return;

  global = getGlobal();
  global = replaceSettings(global, result);
  setGlobal(global);
});

addActionHandler('updateContentSettings', async (global, actions, payload): Promise<void> => {
  global = replaceSettings(global, { isSensitiveEnabled: payload });
  setGlobal(global);

  const result = await callApi('updateContentSettings', payload);
  if (!result) {
    global = getGlobal();
    global = replaceSettings(global, { isSensitiveEnabled: !payload });
    setGlobal(global);
  }
});

addActionHandler('loadCountryList', async (global, actions, payload): Promise<void> => {
  let { langCode } = payload;
  if (!langCode) langCode = global.settings.byKey.language;

  const countryList = await callApi('fetchCountryList', { langCode });
  if (!countryList) return;

  global = getGlobal();
  global = {
    ...global,
    countryList,
  };
  setGlobal(global);
});

addActionHandler('ensureTimeFormat', async (global, actions): Promise<void> => {
  if (global.authNearestCountry) {
    const timeFormat = COUNTRIES_WITH_12H_TIME_FORMAT
      .has(global.authNearestCountry.toUpperCase()) ? '12h' : '24h';
    actions.setSettingOption({ timeFormat });
    setTimeFormat(timeFormat);
  }

  if (global.settings.byKey.wasTimeFormatSetManually) {
    return;
  }

  const nearestCountryCode = await callApi('fetchNearestCountry');
  if (nearestCountryCode) {
    const timeFormat = COUNTRIES_WITH_12H_TIME_FORMAT.has(nearestCountryCode.toUpperCase()) ? '12h' : '24h';
    actions.setSettingOption({ timeFormat });
    setTimeFormat(timeFormat);
  }
});

addActionHandler('loadAppConfig', async (global, actions, payload): Promise<void> => {
  const hash = payload?.hash;

  const appConfig = await callApi('fetchAppConfig', hash);
  if (!appConfig) return;

  requestActionTimeout({
    action: 'loadAppConfig',
    payload: { hash: appConfig.hash },
  }, APP_CONFIG_REFETCH_INTERVAL);

  global = getGlobal();
  global = {
    ...global,
    appConfig,
  };
  setGlobal(global);
});

addActionHandler('loadConfig', async (global): Promise<void> => {
  const config = await callApi('fetchConfig');
  if (!config) return;

  global = getGlobal();
  const timeout = config.expiresAt - getServerTime();
  requestActionTimeout({
    action: 'loadConfig',
    payload: undefined,
  }, timeout * 1000);

  global = {
    ...global,
    config,
  };
  setGlobal(global);
});

addActionHandler('loadPeerColors', async (global): Promise<void> => {
  const hash = global.peerColors?.generalHash;
  const result = await callApi('fetchPeerColors', hash);
  if (!result) return;

  global = getGlobal();
  global = {
    ...global,
    peerColors: {
      ...global.peerColors,
      general: result.colors,
      generalHash: result.hash,
    },
  };
  setGlobal(global);
});

addActionHandler('loadCloudThemes', async (global): Promise<void> => {
  const { hash } = global.settings.cloudThemes;
  logCloudThemeWallpaper('loadCloudThemes:start', { hash, format: CLOUD_THEME_FORMAT });
  let result = await callApi('fetchThemes', {
    hash,
    format: CLOUD_THEME_FORMAT,
  });

  if (!result) {
    logCloudThemeWallpaper('loadCloudThemes:not-modified');

    // Some servers may hash only id/slug metadata and miss theme settings changes.
    if (hash !== '0') {
      logCloudThemeWallpaper('loadCloudThemes:force-refresh', { format: CLOUD_THEME_FORMAT });
      result = await callApi('fetchThemes', {
        hash: '0',
        format: CLOUD_THEME_FORMAT,
      });
    }

    if (!result) {
      return;
    }
  }

  isCloudThemeDocumentsSynced = true;
  logCloudThemeWallpaper('loadCloudThemes:result', {
    hash: result.hash,
    themesCount: result.themes.length,
  });

  global = getGlobal();
  const synced = applyFetchedCloudThemes(global, result);
  global = synced.global;
  setGlobal(global);

  const { selectedTheme } = synced;
  if (!selectedTheme) {
    return;
  }

  const preparedWallpaperSettings = await prepareCloudThemeWallpaperSettings(selectedTheme);
  logCloudThemeWallpaper('loadCloudThemes:prepared', {
    cloudThemeId: selectedTheme.id,
    wallpapersToMergeCount: preparedWallpaperSettings.wallpapersToMerge.length,
  });

  global = getGlobal();
  if (global.settings.byKey.cloudThemeId !== selectedTheme.id) {
    return;
  }
  global = applyPreparedCloudThemeWallpaperSettings(global, preparedWallpaperSettings, {
    preserveUserWallpaperOverrides: true,
  });
  setGlobal(global);
});

addActionHandler('selectCloudTheme', async (global, actions, payload): Promise<void> => {
  const { themeId, themeSlug } = payload;
  logCloudThemeWallpaper('selectCloudTheme:start', { themeId, themeSlug });
  const previousCloudThemeId = global.settings.byKey.cloudThemeId;

  let selectedTheme = findCloudTheme(global.settings.cloudThemes.themes, {
    id: themeId,
    slug: themeSlug,
  });
  const requestedThemeSlug = themeSlug || selectedTheme?.slug;

  logCloudThemeWallpaper('selectCloudTheme:resolved-theme', {
    themeId,
    themeSlug,
    selectedThemeId: selectedTheme?.id,
    selectedThemeSlug: selectedTheme?.slug,
  });

  global = replaceSettings(global, {
    cloudThemeId: selectedTheme?.id,
  });
  if (selectedTheme && !previousCloudThemeId) {
    captureThemeSettingsSnapshot(global);
  }

  if (!selectedTheme) {
    logCloudThemeWallpaper('selectCloudTheme:reset-to-default');
    global = restoreThemeSettingsFromSnapshot(global);
    setGlobal(global);
    return;
  }

  setGlobal(global);

  await ensureCloudThemeDocumentsSynced();

  global = getGlobal();
  selectedTheme = findCloudTheme(global.settings.cloudThemes.themes, {
    id: themeId || selectedTheme.id,
    slug: requestedThemeSlug,
  });

  logCloudThemeWallpaper('selectCloudTheme:resolved-after-sync', {
    themeId,
    themeSlug: requestedThemeSlug,
    selectedThemeId: selectedTheme?.id,
    selectedThemeSlug: selectedTheme?.slug,
  });
  if (!selectedTheme) {
    return;
  }

  const currentCloudThemeId = global.settings.byKey.cloudThemeId;
  if (!currentCloudThemeId) {
    return;
  }

  if (currentCloudThemeId !== selectedTheme.id) {
    const currentSelectedTheme = findCloudThemeById(global.settings.cloudThemes.themes, currentCloudThemeId);
    if (currentSelectedTheme?.slug !== requestedThemeSlug) {
      return;
    }

    global = replaceSettings(global, {
      cloudThemeId: selectedTheme.id,
    });
    setGlobal(global);
  }

  const immediateThemeKeys: ThemeKey[] = [global.settings.byKey.theme];
  const preparedWallpaperSettings = await prepareCloudThemeWallpaperSettings(selectedTheme, immediateThemeKeys);
  logCloudThemeWallpaper('selectCloudTheme:prepared', {
    cloudThemeId: selectedTheme.id,
    wallpapersToMergeCount: preparedWallpaperSettings.wallpapersToMerge.length,
    preparedKeys: Object.keys(preparedWallpaperSettings.byTheme),
    isDeferred: false,
  });

  global = getGlobal();
  if (global.settings.byKey.cloudThemeId !== selectedTheme.id) {
    return;
  }
  global = applyPreparedCloudThemeWallpaperSettings(global, preparedWallpaperSettings);
  setGlobal(global);

  global = getGlobal();
  if (global.settings.byKey.cloudThemeId !== selectedTheme.id) {
    return;
  }
  const currentThemeKey = global.settings.byKey.theme;
  logCloudThemeWallpaper('selectCloudTheme:apply-local', {
    cloudThemeId: selectedTheme.id,
    currentThemeKey,
    currentThemeBackground: global.settings.themes[currentThemeKey]?.background,
    currentThemeBackgroundColor: global.settings.themes[currentThemeKey]?.backgroundColor,
  });

  await callApi('installTheme', {
    themeId: selectedTheme.id,
    accessHash: selectedTheme.accessHash,
    isDark: currentThemeKey === 'dark',
    format: CLOUD_THEME_FORMAT,
  });

  const selectedSetting = resolveCloudThemeSettings(selectedTheme, currentThemeKey);
  logCloudThemeWallpaper('selectCloudTheme:install-remote-theme', {
    cloudThemeId: selectedTheme.id,
    themeKey: currentThemeKey,
    wallpaperSlug: selectedSetting?.wallpaper?.slug,
  });
  if (selectedSetting?.wallpaper?.slug) {
    await callApi('installThemeWallpaper', {
      slug: selectedSetting.wallpaper.slug,
      settings: selectedSetting.wallpaperSettings || selectedSetting.wallpaper.settings,
    });
  }

  const deferredThemeKeys = THEME_KEYS.filter((themeKey) => !immediateThemeKeys.includes(themeKey));
  if (!deferredThemeKeys.length) {
    return;
  }

  void (async () => {
    const deferredPrepared = await prepareCloudThemeWallpaperSettings(selectedTheme, deferredThemeKeys);
    logCloudThemeWallpaper('selectCloudTheme:prepared', {
      cloudThemeId: selectedTheme.id,
      wallpapersToMergeCount: deferredPrepared.wallpapersToMerge.length,
      preparedKeys: Object.keys(deferredPrepared.byTheme),
      isDeferred: true,
    });

    global = getGlobal();
    if (global.settings.byKey.cloudThemeId !== selectedTheme.id) {
      return;
    }

    global = applyPreparedCloudThemeWallpaperSettings(global, deferredPrepared);
    setGlobal(global);
  })();
});

async function ensureCloudThemeDocumentsSynced() {
  if (isCloudThemeDocumentsSynced) {
    logCloudThemeWallpaper('ensureCloudThemeDocumentsSynced:skip-already-synced');
    return;
  }

  logCloudThemeWallpaper('ensureCloudThemeDocumentsSynced:fetch');
  const result = await callApi('fetchThemes', {
    hash: '0',
    format: CLOUD_THEME_FORMAT,
  });

  if (result) {
    isCloudThemeDocumentsSynced = true;
    // eslint-disable-next-line eslint-multitab-tt/no-immediate-global
    let global = getGlobal();
    const synced = applyFetchedCloudThemes(global, result);
    global = synced.global;
    setGlobal(global);
    logCloudThemeWallpaper('ensureCloudThemeDocumentsSynced:fetched', {
      hash: result.hash,
      themesCount: result.themes.length,
    });
  } else {
    logCloudThemeWallpaper('ensureCloudThemeDocumentsSynced:fetched-empty');
  }
}

async function prepareCloudThemeWallpaperSettings(
  cloudTheme: ApiCloudTheme,
  themeKeys: ThemeKey[] = THEME_KEYS,
): Promise<{
  byTheme: Partial<Record<ThemeKey, ThemeSettingsUpdate>>;
  wallpapersToMerge: NonNullable<GlobalState['settings']['loadedWallpapers']>;
}> {
  const byTheme: Partial<Record<ThemeKey, ThemeSettingsUpdate>> = {};
  const wallpapersToMerge: NonNullable<GlobalState['settings']['loadedWallpapers']> = [];

  for (const key of themeKeys) {
    const setting = resolveCloudThemeSettings(cloudTheme, key);
    if (!setting) {
      logCloudThemeWallpaper('prepare:skip-no-setting', {
        cloudThemeId: cloudTheme.id,
        themeKey: key,
      });
      continue;
    }

    const defaultPatternColor = key === 'dark' ? DARK_THEME_PATTERN_COLOR : DEFAULT_PATTERN_COLOR;
    const update: ThemeSettingsUpdate = {};
    const wallpaper = setting.wallpaper;
    const wallpaperSettings = setting.wallpaperSettings || wallpaper?.settings;
    const isPatternWallpaper = isWallpaperPattern(wallpaper);
    const wallpaperBackgroundColor = wallpaperSettings?.backgroundColors?.[0];
    let resolvedBackgroundColor: string | undefined;

    if (wallpaperBackgroundColor) {
      resolvedBackgroundColor = wallpaperBackgroundColor;
    } else if (wallpaper?.document.thumbnail) {
      resolvedBackgroundColor = await getBackgroundColorFromWallpaperThumbnail(wallpaper.document.thumbnail.dataUri);
    }

    const effectiveWallpaperSettings = resolveEffectiveWallpaperSettings({
      cloudThemeSetting: setting,
      themeKey: key,
      isPatternWallpaper,
      wallpaperSettings,
      fallbackBackgroundColor: resolvedBackgroundColor,
    });

    const fallbackPatternBackgroundColor = key === 'dark' ? DARK_THEME_PATTERN_BG_COLOR : LIGHT_THEME_BG_COLOR;

    if (!resolvedBackgroundColor) {
      resolvedBackgroundColor = effectiveWallpaperSettings?.backgroundColors?.[0];
    }
    if (!resolvedBackgroundColor && isPatternWallpaper) {
      resolvedBackgroundColor = fallbackPatternBackgroundColor;
    }

    if (wallpaper) {
      wallpapersToMerge.push(wallpaper);
      const { id: documentId } = wallpaper.document;
      update.wallpaperSettings = effectiveWallpaperSettings;
      update.isPatternWallpaper = isPatternWallpaper;
      update.isDarkWallpaper = Boolean(wallpaper.isDark);
      update.wallpaperMimeType = wallpaper.document.mimeType;
      update.isWallpaperOverridden = false;
      logCloudThemeWallpaper('prepare:wallpaper-found', {
        cloudThemeId: cloudTheme.id,
        themeKey: key,
        wallpaperSlug: wallpaper.slug,
        documentId,
        hasWallpaperSettings: Boolean(wallpaperSettings),
        hasEffectiveWallpaperSettings: Boolean(effectiveWallpaperSettings),
        effectiveWallpaperBackgroundColors: effectiveWallpaperSettings?.backgroundColors?.length,
        settingAccentColor: setting.accentColor,
        settingOutboxAccentColor: setting.outboxAccentColor,
        settingMessageColors: setting.messageColors?.length,
        isPattern: isPatternWallpaper,
        mimeType: wallpaper.document.mimeType,
        fallbackBackgroundColor: resolvedBackgroundColor,
      });
      if (documentId && await cacheCloudWallpaper({
        theme: key,
        documentId,
        expectedMimeType: wallpaper.document.mimeType,
        isPattern: isPatternWallpaper,
        settings: effectiveWallpaperSettings,
        fallbackBackgroundColor: resolvedBackgroundColor || fallbackPatternBackgroundColor,
      })) {
        update.background = wallpaper.slug;
        update.backgroundDocumentId = documentId;
      }

      update.isBlurred = Boolean(effectiveWallpaperSettings?.isBlurred);
    } else {
      update.background = undefined;
      update.backgroundDocumentId = undefined;
      update.isBlurred = true;
      update.wallpaperSettings = undefined;
      update.isPatternWallpaper = undefined;
      update.isDarkWallpaper = undefined;
      update.wallpaperMimeType = undefined;
      update.isWallpaperOverridden = false;
    }

    if (resolvedBackgroundColor) {
      update.backgroundColor = resolvedBackgroundColor;
      update.patternColor = getPatternColorFromHex(resolvedBackgroundColor)
        || defaultPatternColor;
    } else {
      update.backgroundColor = undefined;
      update.patternColor = defaultPatternColor;
    }

    if (Object.keys(update).length) {
      byTheme[key] = update;
      logCloudThemeWallpaper('prepare:update', {
        cloudThemeId: cloudTheme.id,
        themeKey: key,
        update,
      });
    }
  }

  return {
    byTheme,
    wallpapersToMerge,
  };
}

function applyPreparedCloudThemeWallpaperSettings<T extends GlobalState>(
  global: T,
  prepared: {
    byTheme: Partial<Record<ThemeKey, ThemeSettingsUpdate>>;
    wallpapersToMerge: NonNullable<GlobalState['settings']['loadedWallpapers']>;
  },
  options?: {
    preserveUserWallpaperOverrides?: boolean;
  },
): T {
  let updatedGlobal = global;

  THEME_KEYS.forEach((themeKey) => {
    const update = prepared.byTheme[themeKey];
    if (update) {
      const previousThemeSettings = updatedGlobal.settings.themes[themeKey];
      if (options?.preserveUserWallpaperOverrides
        && shouldSkipCloudThemeWallpaperUpdate(previousThemeSettings, update)) {
        logCloudThemeWallpaper('applyPrepared:skip-theme-user-override', {
          themeKey,
          update: buildThemeWallpaperDebugSnapshot(update),
          previous: buildThemeWallpaperDebugSnapshot(previousThemeSettings),
        });
        return;
      }

      updatedGlobal = replaceThemeSettings(updatedGlobal, themeKey, update);
      logCloudThemeWallpaper('applyPrepared:update-theme', {
        themeKey,
        update,
        previous: buildThemeWallpaperDebugSnapshot(previousThemeSettings),
        next: buildThemeWallpaperDebugSnapshot(updatedGlobal.settings.themes[themeKey]),
      });
    }
  });

  if (!prepared.wallpapersToMerge.length) {
    return updatedGlobal;
  }

  const loadedWallpapers = updatedGlobal.settings.loadedWallpapers;
  if (!loadedWallpapers?.length) {
    logCloudThemeWallpaper('applyPrepared:skip-merge-no-loaded-wallpapers', {
      pendingCount: prepared.wallpapersToMerge.length,
    });
    return updatedGlobal;
  }

  const mergedByKey = new Map(loadedWallpapers.map((wallpaper) => [getLoadedWallpaperMergeKey(wallpaper), wallpaper]));
  let addedUniqueCount = 0;

  prepared.wallpapersToMerge.forEach((wallpaper) => {
    const mergeKey = getLoadedWallpaperMergeKey(wallpaper);
    if (!mergedByKey.has(mergeKey)) {
      mergedByKey.set(mergeKey, wallpaper);
      addedUniqueCount++;
    }
  });
  logCloudThemeWallpaper('applyPrepared:merge-wallpapers', {
    mergedCount: mergedByKey.size,
    addedCount: prepared.wallpapersToMerge.length,
    addedUniqueCount,
  });

  if (!addedUniqueCount) {
    return updatedGlobal;
  }

  return {
    ...updatedGlobal,
    settings: {
      ...updatedGlobal.settings,
      loadedWallpapers: Array.from(mergedByKey.values()),
    },
  } as T;
}

function shouldSkipCloudThemeWallpaperUpdate(
  currentSettings?: ThemeSettingsUpdate,
  update?: ThemeSettingsUpdate,
) {
  return Boolean(
    currentSettings?.isWallpaperOverridden
    && update
    && hasThemeWallpaperMutation(update),
  );
}

function getLoadedWallpaperMergeKey(
  wallpaper: NonNullable<GlobalState['settings']['loadedWallpapers']>[number],
) {
  const { slug } = wallpaper;
  const documentId = wallpaper.document.id;

  return documentId ? `${slug}:${documentId}` : slug;
}

async function cacheCloudWallpaper({
  theme,
  documentId,
  expectedMimeType,
  isPattern,
  settings,
  fallbackBackgroundColor,
}: {
  theme: ThemeKey;
  documentId: string;
  expectedMimeType?: string;
  isPattern?: boolean;
  settings?: ApiWallpaperSettings;
  fallbackBackgroundColor?: string;
}): Promise<boolean> {
  const mediaHash = `wallpaper${documentId}`;
  const wallpaperSignature = buildCloudWallpaperSignature({
    documentId,
    expectedMimeType,
    isPattern,
    settings,
    fallbackBackgroundColor,
  });
  const signatureCacheKey = getCloudWallpaperSignatureCacheKey(theme);
  const blobCacheKey = getCloudWallpaperBlobCacheKey(wallpaperSignature);
  logCloudThemeWallpaper('cacheCloudWallpaper:start', {
    theme,
    documentId,
    mediaHash,
    expectedMimeType,
    isPattern,
    hasSettings: Boolean(settings),
    fallbackBackgroundColor,
    signature: wallpaperSignature,
  });

  const cachedSignature = await cacheApi.fetch(CUSTOM_BG_CACHE_NAME, signatureCacheKey, cacheApi.Type.Text);
  if (cachedSignature === wallpaperSignature) {
    const cachedWallpaper = await cacheApi.fetch(CUSTOM_BG_CACHE_NAME, theme, cacheApi.Type.Blob);
    if (cachedWallpaper) {
      logCloudThemeWallpaper('cacheCloudWallpaper:skip-signature-hit', {
        theme,
        documentId,
      });
      return true;
    }
  }

  const cachedSharedWallpaper = await cacheApi.fetch(CUSTOM_BG_CACHE_NAME, blobCacheKey, cacheApi.Type.Blob);
  if (cachedSharedWallpaper) {
    const isSavedFromShared = await cacheApi.save(CUSTOM_BG_CACHE_NAME, theme, cachedSharedWallpaper);
    if (isSavedFromShared) {
      await cacheApi.save(CUSTOM_BG_CACHE_NAME, signatureCacheKey, wallpaperSignature);
      logCloudThemeWallpaper('cacheCloudWallpaper:skip-shared-signature-hit', {
        theme,
        documentId,
        blobCacheKey,
      });
      return true;
    }
  }

  for (let attempt = 0; attempt < 2; attempt++) {
    let blobUrl: string | undefined;

    try {
      blobUrl = await mediaLoader.fetch(mediaHash, ApiMediaFormat.BlobUrl);
    } catch {
      // Intentionally ignored: fallback flow below handles retry after refreshing cloud theme docs.
      logCloudThemeWallpaper('cacheCloudWallpaper:mediaLoader-threw', {
        theme,
        documentId,
        attempt,
      });
    }

    if (blobUrl) {
      let blob = await fetchBlob(blobUrl);
      if (!blob.type && expectedMimeType) {
        blob = blob.slice(0, blob.size, expectedMimeType);
      }
      const sourceBlobType = blob.type;
      blob = await getCacheableWallpaperBlob({
        blob,
        mimeType: expectedMimeType || blob.type,
      });
      const isSaved = await cacheApi.save(CUSTOM_BG_CACHE_NAME, theme, blob);
      const isSharedSaved = await cacheApi.save(CUSTOM_BG_CACHE_NAME, blobCacheKey, blob);
      if (isSaved) {
        await cacheApi.save(CUSTOM_BG_CACHE_NAME, signatureCacheKey, wallpaperSignature);
      }
      logCloudThemeWallpaper('cacheCloudWallpaper:success', {
        theme,
        documentId,
        attempt,
        blobSize: blob.size,
        blobType: blob.type,
        sourceBlobType,
        isSaved,
        isSharedSaved,
      });
      return isSaved;
    }
    logCloudThemeWallpaper('cacheCloudWallpaper:no-blob-url', {
      theme,
      documentId,
      attempt,
    });

    if (attempt !== 0) {
      logCloudThemeWallpaper('cacheCloudWallpaper:failed-after-retry', {
        theme,
        documentId,
      });
      return false;
    }

    const result = await callApi('fetchThemes', {
      hash: '0',
      format: CLOUD_THEME_FORMAT,
    });

    if (result) {
      isCloudThemeDocumentsSynced = true;
      logCloudThemeWallpaper('cacheCloudWallpaper:refresh-themes-success', {
        theme,
        documentId,
        hash: result.hash,
      });
    } else {
      logCloudThemeWallpaper('cacheCloudWallpaper:refresh-themes-empty', {
        theme,
        documentId,
      });
    }
  }

  return false;
}

function getCloudWallpaperSignatureCacheKey(theme: ThemeKey) {
  return `${CLOUD_WALLPAPER_SIGNATURE_PREFIX}:${theme}`;
}

function getCloudWallpaperBlobCacheKey(signature: string) {
  return `${CLOUD_WALLPAPER_BLOB_PREFIX}:${signature}`;
}

function buildCloudWallpaperSignature({
  documentId,
  expectedMimeType,
  isPattern,
  settings,
  fallbackBackgroundColor,
}: {
  documentId: string;
  expectedMimeType?: string;
  isPattern?: boolean;
  settings?: ApiWallpaperSettings;
  fallbackBackgroundColor?: string;
}) {
  return [
    WALLPAPER_RENDERER_VERSION,
    documentId,
    expectedMimeType || '',
    isPattern ? '1' : '0',
    settings?.isBlurred ? '1' : '0',
    settings?.intensity ?? '',
    settings?.rotation ?? '',
    settings?.backgroundColors?.join(',') || '',
    fallbackBackgroundColor || '',
  ].join('|');
}

async function getBackgroundColorFromWallpaperThumbnail(thumbnailDataUri: string) {
  if (wallpaperThumbnailColorCache.has(thumbnailDataUri)) {
    return wallpaperThumbnailColorCache.get(thumbnailDataUri);
  }

  try {
    const rgb = await getAverageColor(thumbnailDataUri);
    const color = `#${rgb2hex(rgb)}`;
    wallpaperThumbnailColorCache.set(thumbnailDataUri, color);
    return color;
  } catch {
    wallpaperThumbnailColorCache.set(thumbnailDataUri, undefined);
    return undefined;
  }
}

function getPatternColorFromHex(hexColor: string) {
  try {
    const rgb = hex2rgb(hexColor.replace('#', ''));
    return getPatternColor(rgb);
  } catch {
    return undefined;
  }
}

function resolveEffectiveWallpaperSettings({
  cloudThemeSetting,
  themeKey,
  isPatternWallpaper,
  wallpaperSettings,
  fallbackBackgroundColor,
}: {
  cloudThemeSetting: ApiCloudThemeSettings;
  themeKey: ThemeKey;
  isPatternWallpaper: boolean;
  wallpaperSettings?: ApiWallpaperSettings;
  fallbackBackgroundColor?: string;
}): ApiWallpaperSettings | undefined {
  if (!isPatternWallpaper) {
    return wallpaperSettings;
  }

  const backgroundColors = wallpaperSettings?.backgroundColors?.length
    ? wallpaperSettings.backgroundColors
    : derivePatternBackgroundColors(cloudThemeSetting, themeKey, fallbackBackgroundColor);

  if (!wallpaperSettings && !backgroundColors?.length) {
    return undefined;
  }

  return {
    ...wallpaperSettings,
    backgroundColors,
    intensity: wallpaperSettings?.intensity ?? DEFAULT_PATTERN_WALLPAPER_INTENSITY,
    rotation: wallpaperSettings?.rotation ?? (
      backgroundColors?.length ? DEFAULT_PATTERN_WALLPAPER_ROTATION : undefined
    ),
  };
}

function derivePatternBackgroundColors(
  cloudThemeSetting: ApiCloudThemeSettings,
  themeKey: ThemeKey,
  fallbackBackgroundColor?: string,
) {
  const messageColors = (cloudThemeSetting.messageColors || [])
    .map(normalizeHexColor)
    .filter((color): color is string => Boolean(color));
  if (messageColors.length) {
    return messageColors.slice(0, 4);
  }

  const accentColor = normalizeHexColor(cloudThemeSetting.accentColor);
  const outboxAccentColor = normalizeHexColor(cloudThemeSetting.outboxAccentColor);

  if (accentColor && outboxAccentColor && accentColor !== outboxAccentColor) {
    return [accentColor, outboxAccentColor];
  }

  if (accentColor) {
    const mixedTarget = themeKey === 'dark' ? '#000000' : '#ffffff';
    const mixedColor = mixHexColors(accentColor, mixedTarget, themeKey === 'dark' ? 0.24 : 0.2);
    return [accentColor, mixedColor];
  }

  const normalizedFallback = normalizeHexColor(fallbackBackgroundColor);
  return normalizedFallback ? [normalizedFallback] : undefined;
}

function normalizeHexColor(color?: string) {
  if (!color) {
    return undefined;
  }

  const normalized = color
    .replace('#', '')
    .replace(/[^a-f\d]/gi, '')
    .toLowerCase();

  if (!normalized) {
    return undefined;
  }

  if (normalized.length === 3) {
    const [r, g, b] = normalized;
    return `#${r}${r}${g}${g}${b}${b}`;
  }

  if (normalized.length >= 6) {
    return `#${normalized.slice(0, 6)}`;
  }

  return undefined;
}

function mixHexColors(baseColor: string, mixedColor: string, ratio: number) {
  const base = hex2rgb(baseColor.slice(1));
  const mixed = hex2rgb(mixedColor.slice(1));
  const t = Math.max(0, Math.min(1, ratio));

  return `#${rgb2hex([
    Math.round(base[0] + (mixed[0] - base[0]) * t),
    Math.round(base[1] + (mixed[1] - base[1]) * t),
    Math.round(base[2] + (mixed[2] - base[2]) * t),
  ])}`;
}

addActionHandler('loadTimezones', async (global): Promise<void> => {
  const hash = global.timezones?.hash;
  const result = await callApi('fetchTimezones', hash);
  if (!result) return;

  global = getGlobal();
  global = {
    ...global,
    timezones: {
      byId: buildCollectionByKey(result.timezones, 'id'),
      hash: result.hash,
    },
  };
  setGlobal(global);
});

addActionHandler('loadGlobalPrivacySettings', async (global): Promise<void> => {
  const globalSettings = await callApi('fetchGlobalPrivacySettings');
  if (!globalSettings) {
    return;
  }

  global = getGlobal();
  global = replaceSettings(global, { ...globalSettings });
  setGlobal(global);
});

addActionHandler('updateGlobalPrivacySettings', async (global, actions, payload): Promise<void> => {
  const shouldHideReadMarks = payload.shouldHideReadMarks ?? Boolean(global.settings.byKey.shouldHideReadMarks);

  global = replaceSettings(global, { shouldHideReadMarks });
  setGlobal(global);

  const result = await callApi('updateGlobalPrivacySettings', {
    shouldHideReadMarks,
  });

  global = getGlobal();
  global = replaceSettings(global, {
    shouldHideReadMarks: !result ? !shouldHideReadMarks : result.shouldHideReadMarks,
  });
  setGlobal(global);
});

addActionHandler('toggleUsername', async (global, actions, payload): Promise<void> => {
  const { username, isActive } = payload;
  const { currentUserId } = global;
  if (!currentUserId) {
    return;
  }

  const currentUser = selectUser(global, currentUserId);
  if (!currentUser?.usernames) {
    return;
  }

  const usernames = currentUser.usernames.map((item) => {
    if (item.username !== username) {
      return item;
    }

    return { ...item, isActive: isActive || undefined };
  });

  global = updateUser(global, currentUserId, { usernames });
  setGlobal(global);

  const result = await callApi('toggleUsername', { username, isActive });

  if (!result) {
    actions.loadFullUser({ userId: currentUserId });
  }
});

addActionHandler('toggleChatUsername', async (global, actions, payload): Promise<void> => {
  const {
    chatId, username, isActive,
  } = payload;
  const chat = selectChat(global, chatId);
  if (!chat?.usernames) {
    return;
  }

  const usernames = chat.usernames.map((item) => {
    if (item.username !== username) {
      return item;
    }

    return { ...item, isActive: isActive || undefined };
  });

  global = updateChat(global, chatId, { usernames });
  setGlobal(global);

  const result = await callApi('toggleUsername', {
    chatId: chat.id,
    accessHash: chat.accessHash,
    username,
    isActive,
  });

  if (!result) {
    actions.loadFullChat({ chatId });
  }
});

addActionHandler('sortUsernames', async (global, actions, payload): Promise<void> => {
  const { usernames } = payload;
  const { currentUserId } = global;
  if (!currentUserId) {
    return;
  }

  const result = await callApi('reorderUsernames', { usernames });

  // After saving the order of usernames, server sends an update with the necessary data,
  // so there is no need to update the state in this place
  if (!result) {
    actions.loadUser({ userId: currentUserId });
  }
});

addActionHandler('sortChatUsernames', async (global, actions, payload): Promise<void> => {
  const { chatId, usernames } = payload;
  const chat = selectChat(global, chatId);
  if (!chat) {
    return;
  }

  const prevUsernames = [...chat.usernames!];
  const sortedUsernames = chat.usernames!.reduce((res, currentUsername) => {
    const idx = usernames.findIndex((username) => username === currentUsername.username);
    res[idx] = currentUsername;

    return res;
  }, [] as ApiUsername[]);

  global = updateChat(global, chatId, { usernames: sortedUsernames });
  setGlobal(global);

  const result = await callApi('reorderUsernames', {
    chatId: chat.id,
    accessHash: chat.accessHash,
    usernames,
  });

  if (!result) {
    global = getGlobal();
    global = updateChat(global, chatId, { usernames: prevUsernames });
    setGlobal(global);
  }
});
